import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Card, Spinner, Alert, Button, Form, Badge } from 'react-bootstrap';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faPaperPlane, faClipboardList } from '@fortawesome/free-solid-svg-icons';

const AnamneseTabletPage = () => {
    const { codigo } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFinished, setIsFinished] = useState(false);
    const [error, setError] = useState(null);
    const [pacienteData, setPacienteData] = useState(null);
    const [respostas, setRespostas] = useState({});

    useEffect(() => {
        const validarCodigo = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(`/api/public/anamnese/${codigo}`);
                setPacienteData(response.data);
            } catch (err) {
                setError("Código inválido ou expirado. Por favor, solicite um novo código na recepção.");
            } finally {
                setIsLoading(false);
            }
        };
        validarCodigo();
    }, [codigo]);

    const handleInputChange = (perguntaId, value) => {
        setRespostas(prev => ({ ...prev, [perguntaId]: value }));
    };

    // ✅ Renderiza o input inline (ao lado da pergunta)
    const renderPerguntaInput = (p) => {
        switch (p.tipo_pergunta) {

            case 'booleano':
                return (
                    // Botões grandes e fáceis de tocar no tablet
                    <div className="d-flex gap-2">
                        <Button
                            variant={respostas[p.id] === 'Sim' ? 'success' : 'outline-success'}
                            size="sm"
                            onClick={() => handleInputChange(p.id, 'Sim')}
                            style={{ minWidth: '70px' }}
                        >
                            Sim
                        </Button>
                        <Button
                            variant={respostas[p.id] === 'Não' ? 'danger' : 'outline-danger'}
                            size="sm"
                            onClick={() => handleInputChange(p.id, 'Não')}
                            style={{ minWidth: '70px' }}
                        >
                            Não
                        </Button>
                    </div>
                );

            case 'multipla_escolha':
                const opcoes = p.opcoes
                    ? (typeof p.opcoes === 'string' ? JSON.parse(p.opcoes) : p.opcoes)
                    : [];
                return (
                    <Form.Select
                        size="sm"
                        value={respostas[p.id] || ''}
                        onChange={(e) => handleInputChange(p.id, e.target.value)}
                        required
                        style={{ minWidth: '200px', maxWidth: '320px' }}
                    >
                        <option value="">Selecione...</option>
                        {opcoes.map((opcao, index) => (
                            <option key={index} value={opcao}>{opcao}</option>
                        ))}
                    </Form.Select>
                );

            case 'texto_livre':
            default:
                return (
                    <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Sua resposta..."
                        value={respostas[p.id] || ''}
                        onChange={(e) => handleInputChange(p.id, e.target.value)}
                        required
                        style={{ minWidth: '260px', maxWidth: '420px', resize: 'none' }}
                    />
                );
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const payload = {
                tipo_ficha: pacienteData.tipo_ficha,
                respostas: Object.keys(respostas).map(id => ({
                    pergunta_id: id,
                    resposta: respostas[id]
                }))
            };
            await axios.post(`/api/public/anamnese/${codigo}/submit`, payload);
            setIsFinished(true);
        } catch (err) {
            alert("Erro ao enviar anamnese. Verifique sua conexão.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Estados de carregamento / erro / finalizado ──────────────────────────

    if (isLoading) {
        return (
            <Container className="d-flex justify-content-center align-items-center vh-100">
                <div className="text-center">
                    <Spinner animation="border" variant="primary" className="mb-3" />
                    <h4>Carregando sua ficha...</h4>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    if (isFinished) {
        return (
            <Container className="d-flex justify-content-center align-items-center vh-100">
                <Card className="text-center shadow-lg p-5 border-0" style={{ maxWidth: '500px' }}>
                    <FontAwesomeIcon icon={faCheckCircle} size="5x" className="text-success mb-4" />
                    <h3>Obrigado, {pacienteData?.paciente_nome}!</h3>
                    <p className="lead text-muted">Sua ficha de anamnese foi enviada com sucesso.</p>
                </Card>
            </Container>
        );
    }

    // ── Render principal ─────────────────────────────────────────────────────

    return (
        <Container className="py-4">
            <Card className="shadow-sm border-0">

                {/* ── Cabeçalho com nome da ficha ── */}
                <Card.Header className="bg-primary text-white py-3">
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <FontAwesomeIcon icon={faClipboardList} size="lg" />
                        <h4 className="mb-0">Ficha de Anamnese</h4>
                    </div>
                    <div className="d-flex flex-wrap gap-2 align-items-center mt-1">
                        <small>Paciente: <strong>{pacienteData?.paciente_nome}</strong></small>
                        <span className="opacity-50">|</span>
                        <small>Especialidade: <strong>{pacienteData?.especialidade}</strong></small>
                        {/* ✅ Nome do tipo de ficha */}
                        {pacienteData?.nome_ficha && (
                            <>
                                <span className="opacity-50">|</span>
                                <Badge bg="light" text="primary" className="fs-6 px-2 py-1">
                                    <FontAwesomeIcon icon={faClipboardList} className="me-1" />
                                    {pacienteData.nome_ficha}
                                </Badge>
                            </>
                        )}
                    </div>
                </Card.Header>

                <Card.Body className="p-0">
                    <Alert variant="info" className="rounded-0 mb-0 border-0 border-bottom">
                        Suas respostas ajudam nossa equipe médica a oferecer o melhor tratamento possível.
                    </Alert>

                    <Form onSubmit={handleSubmit}>
                        {/* ✅ Lista de perguntas — pergunta à esquerda, input à direita */}
                        <div className="list-group list-group-flush">
                            {pacienteData?.perguntas?.map((p, index) => (
                                <div
                                    key={p.id}
                                    className="list-group-item px-4 py-3"
                                    style={{
                                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa'
                                    }}
                                >
                                    <div className="d-flex align-items-center justify-content-between gap-4 flex-wrap">

                                        {/* Número + Texto da pergunta */}
                                        <div className="d-flex align-items-center gap-2 flex-grow-1">
                                            <Badge
                                                bg="primary"
                                                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                style={{ width: '28px', height: '28px', fontSize: '0.75rem' }}
                                            >
                                                {index + 1}
                                            </Badge>
                                            <Form.Label
                                                htmlFor={`pergunta-${p.id}`}
                                                className="mb-0 fw-semibold"
                                                style={{ fontSize: '0.95rem' }}
                                            >
                                                {p.pergunta}
                                            </Form.Label>
                                        </div>

                                        {/* Input alinhado à direita */}
                                        <div className="flex-shrink-0">
                                            {renderPerguntaInput(p)}
                                        </div>

                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Botão de envio */}
                        <div className="d-grid p-4">
                            <Button variant="success" size="lg" type="submit" disabled={isSubmitting}>
                                {isSubmitting
                                    ? <Spinner size="sm" />
                                    : <><FontAwesomeIcon icon={faPaperPlane} className="me-2" />Enviar Respostas</>
                                }
                            </Button>
                        </div>
                    </Form>
                </Card.Body>

            </Card>
        </Container>
    );
};

export default AnamneseTabletPage;
